import type { Layout } from '../../../data/types';
import type { PolygonIndex } from '../../../geo/polygonIndex';
import type { OBB, Vec2 } from '../../../geo/types';
import { obbCorners, pointInRing } from '../../../geo/polygon';
import { lotFrame } from '../lotFrame';
import { obbFromFrontEdge } from '../lotObb';
import { createLotPlacer } from '../lotPlacer';
import type { Lot, ReservedFootprint } from '../types';

/** Fitting estimates, not individual property dimensions measured in the painting. */
const FIT = {
  maxPlotDepth: 60,
  maxWarehouseDepth: 16,
  // Shallow or narrow annexes still need their own dry loading elevation and usable yard.
  minWarehouseDepth: 4,
  minWarehouseWidth: 2.25,
  bankInset: 0.2,
  courtyardDepth: 2.5,
  yardRouteWidth: 0.8,
  yardRouteSetback: 0.4,
  step: 0.25,
  shiftStep: 0.1,
} as const;

interface BankHit {
  readonly distance: number;
  readonly point: Vec2;
  readonly tangent: Vec2;
  readonly edge: readonly [Vec2, Vec2];
}

interface BankLotInput {
  readonly house: Lot;
  /** An existing associated rear body to replace, rather than an extra third building. */
  readonly waterward?: Lot | undefined;
  readonly placed: readonly Lot[];
  readonly layout: Layout;
  readonly buildable: PolygonIndex;
  readonly reserved: readonly ReservedFootprint[];
}

function move(p: Vec2, direction: Vec2, distance: number): Vec2 {
  return [p[0] + direction[0] * distance, p[1] + direction[1] * distance];
}

function dot(a: Vec2, b: Vec2): number {
  return a[0] * b[0] + a[1] * b[1];
}

/** First Damrak bank on the original plot axis; other waterways never qualify. */
function firstBank(from: Vec2, dir: Vec2, layout: Layout, maxDistance: number): BankHit | null {
  const ring = layout.water.areas.find((water) => water.id === 'damrak')?.polygon.outer;
  if (!ring) return null;
  let distance = maxDistance;
  let hit: BankHit | null = null;
  ring.forEach((a, i) => {
    const b = ring[(i + 1) % ring.length]!;
    const edge: Vec2 = [b[0] - a[0], b[1] - a[1]];
    const delta: Vec2 = [a[0] - from[0], a[1] - from[1]];
    const cross = dir[0] * edge[1] - dir[1] * edge[0];
    if (Math.abs(cross) < 1e-8) return;
    const t = (delta[0] * edge[1] - delta[1] * edge[0]) / cross;
    const u = (delta[0] * dir[1] - delta[1] * dir[0]) / cross;
    if (t < 0 || t >= distance || u < 0 || u > 1) return;
    distance = t;
    const length = Math.hypot(...edge);
    let tangent: Vec2 = [edge[0] / length, edge[1] / length];
    if (dot(tangent, [dir[1], -dir[0]]) < 0) tangent = [-tangent[0], -tangent[1]];
    hit = { point: move(from, dir, t), tangent, edge: [a, b], distance: t };
  });
  return hit;
}

/** Distance from the rear midpoint toward the Damrak, not toward a different canal or street. */
export function damrakWaterGap(lot: Lot, layout: Layout): number {
  const ring = layout.water.areas.find((water) => water.id === 'damrak')?.polygon.outer;
  if (!ring) return Infinity;
  const frame = lotFrame(lot);
  const back = move(frame.frontMid, frame.facing, -frame.depth);
  if (pointInRing(back, ring)) return Infinity;
  const direction: Vec2 = [-frame.facing[0], -frame.facing[1]];
  const hit = firstBank(back, direction, layout, FIT.maxPlotDepth);
  if (!hit || !pointInRing(move(hit.point, direction, 0.05), ring)) return Infinity;
  return hit.distance;
}

/** The surveyed rectangle fitting allows up to one metre between a rear wall and the bank. */
export function isDamrakWaterEnd(lot: Lot, layout: Layout): boolean {
  return damrakWaterGap(lot, layout) <= 1;
}

function rectangle(front: Vec2, right: Vec2, facing: Vec2, width: number, depth: number): OBB {
  return obbFromFrontEdge(
    move(front, right, -width / 2),
    move(front, right, width / 2),
    depth,
    facing
  );
}

/**
 * Fit a bank-ending warehouse within one inferred Warmoesstraat plot. A dry rectangle must
 * stay inside the original street-house strip, beyond its courtyard, and clear every currently
 * placed lot and civic reservation. A clear yard route proves it stays on the original alley
 * side. Failure leaves the short street-edge body unchanged; it does not invent another plot.
 */
export function fitDamrakBankLot(input: BankLotInput): Lot | null {
  const { house, waterward, placed, layout, buildable, reserved } = input;
  if (house.kind !== 'house' || house.sourceId !== 'street-warmoesstraat' || !house.reconstruction)
    return null;
  if (waterward && waterward.reconstruction?.id !== `${house.reconstruction.id}/water`) return null;
  const frame = lotFrame(house);
  const inward: Vec2 = [-frame.facing[0], -frame.facing[1]];
  const hit = firstBank(frame.frontMid, inward, layout, FIT.maxPlotDepth);
  if (!hit || hit.distance <= house.depth) return null;
  const strip = obbCorners(
    rectangle(frame.frontMid, frame.right, frame.facing, frame.width, FIT.maxPlotDepth)
  );
  const otherLots = placed.filter((lot) => lot !== house && lot !== waterward);
  const placer = createLotPlacer((p) => buildable.contains(p), otherLots, reserved, buildable);
  const insidePlot = (p: Vec2) => pointInRing(p, strip);
  const depthOnPlot = (p: Vec2) =>
    dot([p[0] - frame.frontMid[0], p[1] - frame.frontMid[1]], inward);
  let facing: Vec2 = [-hit.tangent[1], hit.tangent[0]];
  if (dot(facing, frame.facing) < 0) facing = [-facing[0], -facing[1]];
  const bankCentre = move(hit.point, facing, FIT.bankInset);
  const bankEnds = hit.edge.map((p) =>
    dot([p[0] - hit.point[0], p[1] - hit.point[1]], hit.tangent)
  );
  const yardStart = move(frame.frontMid, inward, house.depth + FIT.yardRouteSetback);

  const connectsThroughYard = (front: Vec2): boolean => {
    const yardEnd = move(front, facing, FIT.yardRouteSetback);
    const delta: Vec2 = [yardEnd[0] - yardStart[0], yardEnd[1] - yardStart[1]];
    const distance = Math.hypot(...delta);
    if (distance <= 0) return false;
    const direction: Vec2 = [delta[0] / distance, delta[1] / distance];
    const right: Vec2 = [-direction[1], direction[0]];
    const corridor = rectangle(
      yardStart,
      right,
      [-direction[0], -direction[1]],
      FIT.yardRouteWidth,
      distance
    );
    return obbCorners(corridor).every(insidePlot) && placer.fits(corridor);
  };

  let best: { readonly obb: OBB; readonly width: number; readonly depth: number } | null = null;
  const maxWidth = Math.floor(frame.width / FIT.step) * FIT.step;
  const shiftCount = Math.ceil(frame.width / FIT.shiftStep);
  for (let width = maxWidth; width >= FIT.minWarehouseWidth; width -= FIT.step) {
    if (best && width * FIT.maxWarehouseDepth <= best.width * best.depth) break;
    // Test the original bank axis first, then symmetric offsets; fitting remains deterministic.
    for (let si = 0; si <= shiftCount * 2; si++) {
      const shift = si === 0 ? 0 : Math.ceil(si / 2) * FIT.shiftStep * (si % 2 ? 1 : -1);
      if (shift - width / 2 < Math.min(...bankEnds) || shift + width / 2 > Math.max(...bankEnds))
        continue;
      const back = move(bankCentre, hit.tangent, shift);
      for (let depth = FIT.maxWarehouseDepth; depth >= FIT.minWarehouseDepth; depth -= FIT.step) {
        if (best && width * depth <= best.width * best.depth) break;
        const front = move(back, facing, depth);
        const obb = rectangle(front, hit.tangent, facing, width, depth);
        const corners = obbCorners(obb);
        if (!corners.every(insidePlot)) continue;
        if (!corners.every((p) => depthOnPlot(p) >= house.depth + FIT.courtyardDepth)) continue;
        if (!placer.fits(obb) || !connectsThroughYard(front)) continue;
        best = { obb, width, depth };
        break;
      }
    }
  }
  if (!best) return null;
  return {
    index: waterward?.index ?? placed.length,
    sourceId: `${house.sourceId}/water`,
    side: house.side,
    ordinal: house.ordinal,
    kind: 'backhouse',
    obb: best.obb,
    corners: obbCorners(best.obb),
    frontage: best.width,
    depth: best.depth,
    facing,
    reconstruction: {
      ...house.reconstruction,
      id: `${house.reconstruction.id}/water`,
      evidence: 'inferred',
      waterfront: 'damrak',
    },
  };
}
